import { useEffect, useState } from 'react';
import { useApi } from '../../services/api';
import { Link } from 'react-router-dom';
import { GameStatus } from '@bst-stellar/shared';
import { useToast } from '../../components/Toast';
import { useTenant } from '../../contexts/TenantContext';
import { formatDate } from '../../utils/dateFormatter';

export default function GameManagement() {
    const api = useApi();
    const { showToast } = useToast();
    const [games, setGames] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'active' | 'draft'>('all');

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedGame, setSelectedGame] = useState<any>(null);

    useEffect(() => {
        loadGames();
    }, [filter]);

    async function loadGames() {
        try {
            setLoading(true);
            const result = await api.getGames();
            let filtered = result.games || [];

            if (filter === 'active') {
                filtered = filtered.filter((g: any) => g.status === GameStatus.ACTIVE);
            } else if (filter === 'draft') {
                filtered = filtered.filter((g: any) => g.status === GameStatus.DRAFT);
            }

            setGames(filtered);
        } catch (error) {
            console.error('Failed to load games:', error);
        } finally {
            setLoading(false);
        }
    }

    async function handleStartGame(gameId: string) {
        try {
            await api.startGame(gameId);
            await loadGames();
            showToast('Game started successfully!', 'success');
        } catch (error: any) {
            console.error('Failed to start game:', error);
            showToast(error.message || 'Failed to start game', 'error');
        }
    }

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading games...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-text-primary">Game Management</h1>
                <Link
                    to="/admin/games/new"
                    className="bg-primary text-white px-4 py-2 rounded hover:opacity-90"
                >
                    + Create Game
                </Link>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded ${filter === 'all'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    All Games ({games.length})
                </button>
                <button
                    onClick={() => setFilter('active')}
                    className={`px-4 py-2 rounded ${filter === 'active'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    Active
                </button>
                <button
                    onClick={() => setFilter('draft')}
                    className={`px-4 py-2 rounded ${filter === 'draft'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-border hover:bg-background'
                        }`}
                >
                    Drafts
                </button>
            </div>

            {/* Games List */}
            {games.length === 0 ? (
                <div className="bg-surface rounded-lg shadow p-12 text-center border border-border">
                    <p className="text-text-secondary text-lg mb-4">No games found</p>
                    <Link
                        to="/admin/games/new"
                        className="text-primary hover:underline"
                    >
                        Create your first game
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {games.map((game) => (
                        <GameCard
                            key={game.id}
                            game={game}
                            onStart={handleStartGame}
                            onRefresh={loadGames}
                            onEdit={(g) => {
                                setSelectedGame(g);
                                setShowEditModal(true);
                            }}
                        />
                    ))}
                </div>
            )}

            {showEditModal && selectedGame && (
                <EditGameModal
                    game={selectedGame}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedGame(null);
                    }}
                    onSuccess={loadGames}
                />
            )}
        </div>
    );
}

interface GameCardProps {
    game: any;
    onStart: (gameId: string) => void;
    onRefresh: () => void;
    onEdit: (game: any) => void;
}

function GameCard({ game, onStart, onEdit }: GameCardProps) {
    const statusColors = {
        draft: 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
        active: 'bg-green-500/20 text-green-400 border border-green-500/30',
        completed: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
        archived: 'bg-gray-500/20 text-gray-500 border border-gray-500/30',
    };

    return (
        <div className="bg-surface rounded-lg shadow p-6 hover:shadow-lg transition border border-border">
            <div className="flex justify-between items-start mb-3">
                <h3 className="text-xl font-bold text-text-primary">{game.name}</h3>
                <span
                    className={`px-2 py-1 text-xs font-semibold rounded ${statusColors[game.status as keyof typeof statusColors]
                        }`}
                >
                    {game.status.toUpperCase()}
                </span>
            </div>

            <p className="text-text-muted text-sm mb-4 line-clamp-2">
                {game.description || 'No description'}
            </p>

            <div className="text-sm text-text-secondary space-y-1 mb-4">
                <div>📅 {formatDate(game.startDate)} - {formatDate(game.endDate)}</div>
                <div>🏢 Department: {game.departmentName || 'Unknown'}</div>
            </div>

            <div className="flex gap-2">
                <Link
                    to={`/admin/games/${game.id}`}
                    className="flex-1 text-center bg-background text-text-secondary px-3 py-2 rounded hover:bg-border border border-border text-sm"
                >
                    View Details
                </Link>
                {game.status === GameStatus.DRAFT && (
                    <>
                        <button
                            onClick={() => onEdit(game)}
                            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
                        >
                            Edit
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                onStart(game.id);
                            }}
                            className="flex-1 bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700 text-sm"
                        >
                            Start
                        </button>
                    </>
                )}
            </div>
        </div >
    );
}

function EditGameModal({ game, onClose, onSuccess }: { game: any; onClose: () => void; onSuccess: () => void }) {
    const api = useApi();
    const { showToast } = useToast();
    const { tenant } = useTenant();
    const [formData, setFormData] = useState({
        name: game.name,
        description: game.description || '',
        startDate: new Date(game.startDate).toISOString().split('T')[0],
        endDate: new Date(game.endDate).toISOString().split('T')[0],
        revealSchedule: {
            enabled: game.revealSchedule?.enabled ?? true,
            day: game.revealSchedule?.day || 'friday',
            time: game.revealSchedule?.time || '17:00',
            durationHours: game.revealSchedule?.durationHours || 24,
        },
    });
    const [submitting, setSubmitting] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            setSubmitting(true);
            await api.updateGame(game.id, formData);
            showToast('Game updated successfully', 'success');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Failed to update game:', error);
            showToast(error.message || 'Failed to update game', 'error');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-lg max-w-lg w-full p-6 shadow-xl border border-border">
                <h2 className="text-xl font-bold text-text-primary mb-4">Edit Game</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Name</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary"
                            rows={3}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">Start Date</label>
                            <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-1">End Date</label>
                            <input
                                type="date"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary"
                                required
                            />
                        </div>
                    </div>

                    <div className="border-t border-border pt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-text-primary">Reveal Schedule</h3>
                            <span className="text-xs text-text-secondary bg-background px-2 py-1 rounded border border-border">
                                🌍 {(tenant as any)?.timezone || 'America/New_York'}
                            </span>
                        </div>

                        {/* Enable Toggle */}
                        <div className="flex items-center gap-3 mb-4 p-3 bg-background rounded-md border border-border">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.revealSchedule.enabled}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        revealSchedule: { ...formData.revealSchedule, enabled: e.target.checked }
                                    })}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                            <div>
                                <span className="font-medium text-text-primary text-sm">Enable Reveal Schedule</span>
                                <p className="text-xs text-text-secondary">
                                    {formData.revealSchedule.enabled
                                        ? 'Leaderboard visible only during reveal times'
                                        : 'Leaderboard always visible'}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Day</label>
                                <select
                                    value={formData.revealSchedule.day}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        revealSchedule: { ...formData.revealSchedule, day: e.target.value }
                                    })}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary text-sm"
                                >
                                    <option value="monday">Monday</option>
                                    <option value="tuesday">Tuesday</option>
                                    <option value="wednesday">Wednesday</option>
                                    <option value="thursday">Thursday</option>
                                    <option value="friday">Friday</option>
                                    <option value="saturday">Saturday</option>
                                    <option value="sunday">Sunday</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Time</label>
                                <input
                                    type="time"
                                    value={formData.revealSchedule.time}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        revealSchedule: { ...formData.revealSchedule, time: e.target.value }
                                    })}
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-text-primary text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-text-secondary hover:text-text-primary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:opacity-50"
                        >
                            {submitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

